import type { ReactNode } from "react";
import type { ClubView, NewsRow, PlayerRow, Sector, TableRow } from "../game/types";
import { Classificacao } from "../ui/Classificacao";
import type { DadosDeCartao } from "../ui/cartoes";
import {
  Bar,
  Crest,
  Money,
  MoraleFace,
  Panel,
  PositionChip,
  Stars,
  StrengthBadge,
} from "../ui/atoms";
import { Bandeira } from "../ui/Bandeira";
import { Icone } from "../ui/icons";
import {
  CabecalhoDaTabela,
  CelulasDaLinha,
  ControlesDaTabela,
  LinhaVazia,
  useTabela,
  type Coluna,
  type Tabela,
} from "../ui/tabela";
import {
  dinheiro,
  numero,
  rotuloDeCaracteristica,
  rotuloDeSetor,
  rotuloDoMotor,
  t as texto,
  useT,
  type Chave,
} from "../i18n";

const SECTOR_ORDER: Sector[] = ["GOL", "DEF", "MEI", "ATA"];

/** Média por rodada só existe depois da primeira rodada ser jogada. */
function perRound(value: number | null): string {
  return value === null ? texto("comum.vazio") : dinheiro(value);
}

/**
 * Elenco agrupado por setor, como nos conceitos.
 *
 * As colunas são as do mapeamento da tela ADM do Clube (25/09/2026): tipo,
 * posição, jogador, força, moral, físico, escalação e salário. Número e gols
 * saíram: o número da camisa não decide nada, e os gols moram na ficha e nos
 * relatórios. O físico ficou porque sem ele não se escala direito.
 *
 * São oito colunas, e oito não cabem num celular. Quem decide isso é
 * `ui/tabela`: no computador saem as nove, e no celular saem as três que dizem
 * de quem é a linha mais a que a pessoa escolher. A ordenação acontece dentro
 * de cada setor, e não sobre o elenco inteiro, porque a pergunta que se faz
 * aqui é quem é o melhor zagueiro, e não quem é o melhor jogador.
 */
export function SquadPanel({
  view,
  onOpenPlayer,
  selecionado,
}: {
  view: ClubView;
  onOpenPlayer: (playerId: string) => void;
  /** Jogador aberto na ficha ao lado, quando ela existe. */
  selecionado?: string | undefined;
}): ReactNode {
  const t = useT();
  const colunas = useColunasDoElenco();

  const tabela = useTabela<PlayerRow>({
    colunas,
    // Nome e nacionalidade bastam: é por eles que se procura alguém num
    // elenco, e a posição já tem chip e agrupamento próprios.
    procurar: (player) => `${player.name} ${player.nationality}`,
  });

  const porSetor = SECTOR_ORDER.map((sector) => ({
    sector,
    players: tabela.preparar(view.squad.filter((player) => player.sector === sector)),
  })).filter((grupo) => grupo.players.length > 0);

  return (
    <Panel title={t("elenco.titulo")} padded={false}>
      <ControlesDaTabela tabela={tabela} />
      <div className="scroll" style={{ maxHeight: "calc(100vh - 300px)" }}>
        <table className="fixa">
          <CabecalhoDaTabela tabela={tabela} />
          <tbody>
            {porSetor.map(({ sector, players }) => (
              <SectorGroup
                key={sector}
                sector={sector}
                players={players}
                tabela={tabela}
                onOpenPlayer={onOpenPlayer}
                selecionado={selecionado}
              />
            ))}
            {porSetor.length === 0 && <LinhaVazia tabela={tabela} />}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/** Escalação, e a ordem em que ela se lê: titular, reserva, lesionado, suspenso, fora. */
function postoNaEscalacao(player: PlayerRow): number {
  if (player.starter) return 0;
  if (player.benched) return 1;
  if (player.injuryRounds > 0) return 2;
  if (player.suspensionRounds > 0) return 3;
  return 4;
}

function useColunasDoElenco(): Array<Coluna<PlayerRow>> {
  const t = useT();

  return [
    {
      /*
       * O tipo do jogador: a figura do setor, numa coluna própria.
       *
       * Luva, escudo, engrenagem e chuteira separam o elenco em quatro blocos
       * sem ninguém precisar ler nada. É também onde o selo do Jogador Estrela
       * vai morar, quando ele existir.
       */
      chave: "tipo",
      rotulo: t("elenco.colunas.tipo"),
      presenca: "sempre",
      largura: 40,
      celula: (player) => (
        <span
          style={{ color: "var(--text-dim)", display: "inline-flex", position: "relative" }}
          title={player.star ? `${rotuloDeSetor(player.sector)} · ${t("timeB.jogadorEstrela")}` : rotuloDeSetor(player.sector)}
        >
          <Icone nome={`Setor${player.sector}`} size={15} />
          {player.star && (
            <span className="selo-estrela" aria-label={t("timeB.jogadorEstrela")}>
              ★
            </span>
          )}
        </span>
      ),
    },
    {
      chave: "pos",
      rotulo: t("elenco.colunas.pos"),
      presenca: "sempre",
      largura: 56,
      celula: (player) => <PositionChip pos={player.pos} />,
    },
    {
      chave: "jogador",
      rotulo: t("elenco.colunas.jogador"),
      presenca: "sempre",
      /*
       * Largura própria, e não a sobra da tabela.
       *
       * Era a única coluna sem largura, e com `table-layout: fixed` isso a
       * fazia engolir tudo o que sobrasse: numa tela de 1288 o nome ficava com
       * 854 pixels para escrever "Rodrigo Marinho". Com largura declarada aqui,
       * a sobra se reparte entre todas, e nenhuma fica com um vão de meia tela.
       *
       * 230 é onde "Domingos Gonçalves Werneck", o nome mais longo que o
       * gerador monta, ainda cabe na primeira linha.
       */
      largura: 230,
      ordenar: (player) => player.name,
      direcaoInicial: "asc",
      celula: (player) => <NomeDoJogador player={player} />,
    },
    {
      chave: "forca",
      rotulo: t("elenco.colunas.forca"),
      presenca: "destaque",
      largura: 46,
      direita: true,
      ordenar: (player) => player.strength,
      celula: (player) => <StrengthBadge value={player.strength} />,
    },
    {
      chave: "moral",
      rotulo: t("elenco.colunas.moral"),
      presenca: "destaque",
      /* 40 não cabia "MORAL", e o rótulo invadia a coluna do físico. */
      largura: 52,
      ordenar: (player) => player.morale,
      celula: (player) => <MoraleFace value={player.morale} />,
    },
    {
      chave: "fisico",
      rotulo: t("elenco.colunas.fisico"),
      presenca: "destaque",
      largura: 84,
      ordenar: (player) => player.condition,
      celula: (player) => <Bar value={player.condition} label={t("comum.condicao")} />,
    },
    {
      chave: "escalacao",
      rotulo: t("elenco.colunas.escalacao"),
      presenca: "destaque",
      largura: 104,
      ordenar: postoNaEscalacao,
      direcaoInicial: "asc",
      celula: (player) => <SituacaoNaEscalacao player={player} />,
    },
    {
      chave: "salario",
      rotulo: t("elenco.colunas.salario"),
      presenca: "destaque",
      largura: 92,
      direita: true,
      ordenar: (player) => player.salary,
      celula: (player) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Money value={player.salary} />
        </span>
      ),
    },
  ];
}

function SectorGroup({
  sector,
  players,
  tabela,
  onOpenPlayer,
  selecionado,
}: {
  sector: Sector;
  players: PlayerRow[];
  tabela: Tabela<PlayerRow>;
  onOpenPlayer: (playerId: string) => void;
  selecionado?: string | undefined;
}): ReactNode {
  return (
    <>
      <tr className="sector-row">
        <td colSpan={tabela.visiveis.length}>
          <span className="label">{rotuloDeSetor(sector)}</span>
          <span className="label-muted" style={{ marginLeft: 8 }}>
            {players.length}
          </span>
        </td>
      </tr>
      {players.map((player) => (
        <PlayerRowView
          key={player.id}
          player={player}
          tabela={tabela}
          onOpen={onOpenPlayer}
          selecionado={player.id === selecionado}
        />
      ))}
    </>
  );
}

/* Idade e destaques descem para a segunda linha. Cabem onde duas colunas
   próprias não cabiam, e o nome deixa de quebrar em duas. */
function NomeDoJogador({ player }: { player: PlayerRow }): ReactNode {
  const t = useT();
  return (
    <div style={{ minWidth: 0 }}>
      <div className="row corta" style={{ fontWeight: 600, gap: 6 }}>
        <span className="corta">{player.name}</span>
        {/*
          Lesão e suspensão em figura, e não em palavra.

          Eram "lesionado 3" e "suspenso" escritos ao lado do nome, e num nome
          longo eles caíam fora da coluna antes de serem lidos. A cruz e o
          cartão vermelho dizem o mesmo em treze pixels, e o número de rodadas
          continua ao lado porque é ele que decide se vale esperar.
        */}
        {player.injuryRounds > 0 && (
          <span
            className="row"
            style={{ gap: 3, color: "var(--danger)", fontSize: 10, flexShrink: 0 }}
            title={t("elenco.lesionado", { rodadas: player.injuryRounds })}
          >
            <Icone nome="Lesao" size={12} />
            <span className="num">{player.injuryRounds}</span>
          </span>
        )}
        {player.suspensionRounds > 0 && (
          <span
            className="row"
            style={{ gap: 3, color: "var(--warning)", fontSize: 10, flexShrink: 0 }}
            title={t("elenco.suspenso")}
          >
            <CartaoVermelho />
            <span className="num">{player.suspensionRounds}</span>
          </span>
        )}
      </div>
      <div className="muted corta row" style={{ fontSize: 10.5, gap: 5 }}>
        <Bandeira codigo={player.nationality} />
        {t("elenco.linhaSecundaria", {
          idade: player.age,
          destaques: player.highlights.map((key) => rotuloDeCaracteristica(key)).join(" · "),
        })}
      </div>
    </div>
  );
}

/**
 * A escalação por extenso: Titular, Reserva, Lesionado, Suspenso ou Fora.
 *
 * Eram três figuras (titular, banco, fora), e lesão e suspensão só apareciam
 * ao lado do nome. O mapeamento da tela pede as cinco situações numa coluna
 * só, com palavra, porque é essa coluna que se varre antes de escalar. A
 * rodada de volta do lesionado vai junto: é ela que decide se vale esperar.
 */
function SituacaoNaEscalacao({ player }: { player: PlayerRow }): ReactNode {
  const t = useT();
  const situacao = player.starter
    ? { texto: t("elenco.escalacao.titular"), classe: "titular" }
    : player.benched
      ? { texto: t("elenco.escalacao.reserva"), classe: "reserva" }
      : player.injuryRounds > 0
        ? { texto: t("elenco.escalacao.lesionado", { rodadas: player.injuryRounds }), classe: "lesionado" }
        : player.suspensionRounds > 0
          ? { texto: t("elenco.escalacao.suspenso", { rodadas: player.suspensionRounds }), classe: "suspenso" }
          : { texto: t("elenco.escalacao.fora"), classe: "fora" };

  return <span className={`chip-escalacao ${situacao.classe}`}>{situacao.texto}</span>;
}

/**
 * O cartão vermelho da suspensão.
 *
 * Desenhado e não figura de catálogo: o Game Icons tem cartas de baralho e não
 * cartão de árbitro, e um retângulo vermelho em pé é exatamente o que um
 * cartão é. Qualquer figura com mais traço que isto diria menos.
 */
function CartaoVermelho(): ReactNode {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 8,
        height: 11,
        borderRadius: 1.5,
        background: "var(--danger)",
        flexShrink: 0,
      }}
    />
  );
}

function PlayerRowView({
  player,
  tabela,
  onOpen,
  selecionado,
}: {
  player: PlayerRow;
  tabela: Tabela<PlayerRow>;
  onOpen: (playerId: string) => void;
  selecionado?: boolean;
}): ReactNode {
  const unavailable = player.injuryRounds > 0 || player.suspensionRounds > 0;
  return (
    <tr
      className={`player${player.starter ? " starter" : ""}`}
      onClick={() => onOpen(player.id)}
      /* `aria-selected` e não só a cor: quem usa leitor de tela precisa ouvir
         qual linha está aberta na ficha ao lado. */
      aria-selected={selecionado}
      style={{ opacity: unavailable ? 0.5 : 1, cursor: "pointer" }}
    >
      <CelulasDaLinha tabela={tabela} linha={player} />
    </tr>
  );
}

export function TablePanel({ view }: { view: ClubView }): ReactNode {
  return (
    <StandingsTable
      title={view.club.leagueName}
      rows={view.table}
      maxHeight="calc(100vh - 300px)"
    />
  );
}

/**
 * A classificação da carreira.
 *
 * Serve a liga e os grupos da continental, que têm exatamente as mesmas
 * colunas.
 *
 * Só traduz o que o motor devolve para a linha que `Classificacao` entende. O
 * desenho mora lá, num lugar só: esta tabela e a da liga online eram a mesma
 * coisa escrita duas vezes, e já tinham divergido, porque a coluna escolhível
 * que faz a tabela caber num celular só chegou nesta.
 */
export function StandingsTable({
  title,
  rows,
  action,
  qualify = 0,
  maxHeight,
}: {
  title: string;
  rows: TableRow[];
  action?: ReactNode;
  /** Quantos primeiros avançam. Zero não desenha corte. */
  qualify?: number;
  maxHeight?: string;
}): ReactNode {
  return (
    <Classificacao
      titulo={title}
      acao={action}
      alturaMaxima={maxHeight}
      linhas={rows.map((linha) => ({
        clubId: linha.clubId,
        posicao: linha.position,
        nome: linha.name,
        cor: linha.colors[0],
        jogos: linha.played,
        vitorias: linha.wins,
        empates: linha.draws,
        derrotas: linha.losses,
        golsPro: linha.goalsFor,
        golsContra: linha.goalsAgainst,
        pontos: linha.points,
        minha: linha.isMine,
        classifica: qualify > 0 && linha.position <= qualify,
      }))}
    />
  );
}

/**
 * As três chaves com que o motor anuncia um reforço fechado.
 *
 * Antes isto era o texto "Contratação fechada", comparado com o título da
 * notícia, e o corpo era fatiado com `indexOf` para achar o valor do negócio.
 * Funcionava enquanto existisse um idioma só: bastava a redação mudar para o
 * botão de compartilhar sumir sem ninguém perceber. A chave é estável e o valor
 * chega como parâmetro, então nada aqui depende mais de como a frase está
 * escrita.
 */
const CHAVES_DE_CONTRATACAO = new Set([
  "mercado.contratacaoLivre",
  "mercado.contratacaoAVista",
  "mercado.contratacaoParcelada",
]);

/**
 * Cartão de contratação montado a partir da notícia do reforço.
 *
 * A notícia traz o nome e o negócio, mas cartão bonito precisa dos números do
 * jogador, então o elenco é quem preenche força, idade e destaques. Quando o
 * reforço já foi embora e não está mais no elenco, o cartão não é oferecido:
 * melhor não ter botão do que ter um cartão pela metade.
 */
export function cartaoDaContratacao(view: ClubView, item: NewsRow): DadosDeCartao | null {
  if (item.key === null || !CHAVES_DE_CONTRATACAO.has(item.key)) return null;

  const nome = String(item.params.jogador ?? "");
  const jogador = view.squad.find((atleta) => atleta.name === nome);
  if (!jogador) return null;

  const valor = item.params.valor;
  const parcelas = item.params.parcelas;
  const negocio =
    valor === undefined
      ? texto("motor.cartaoDeContratacao.semCusto")
      : parcelas === undefined
        ? texto("motor.cartaoDeContratacao.aVista", { valor })
        : texto("motor.cartaoDeContratacao.parcelado", { valor, parcelas });

  return {
    tipo: "contratacao",
    contexto: texto("noticias.contextoDoCartao", { temporada: view.season, rodada: item.round }),
    clube: { nome: view.club.name, sigla: view.club.short, cores: view.club.colors },
    jogador: jogador.name,
    posicao: jogador.pos,
    forca: jogador.strength,
    idade: jogador.age,
    negocio: negocio || texto("noticias.reforcoConfirmado"),
    destaques: jogador.highlights.map((chave) => rotuloDeCaracteristica(chave)),
  };
}

/** Finanças detalhadas da temporada. */
export function FinancePanel({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const { finance, club } = view;
  const balance =
    finance.incomePerRound === null || finance.expensePerRound === null
      ? null
      : finance.incomePerRound - finance.expensePerRound;
  const lines: Array<[Chave, number, boolean]> = [
    ["financas.bilheteria", finance.gate, false],
    ["financas.patrocinio", finance.sponsor, false],
    ["financas.tv", finance.tv, false],
    ["financas.premiacao", finance.prizes, false],
    ["financas.folha", -finance.wagesPerRound, true],
  ];

  return (
    <Panel title={t("financas.titulo")}>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", marginBottom: 16 }}
      >
        <div>
          <div className="label-muted">{t("financas.saldoEmCaixa")}</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2 }}>
            <Money value={club.cash} />
          </div>
        </div>
        <div>
          <div className="label-muted">{t("financas.receitaPorRodada")}</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2, color: "var(--success)" }}>
            {perRound(finance.incomePerRound)}
          </div>
        </div>
        <div>
          <div className="label-muted">{t("financas.despesaPorRodada")}</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2, color: "var(--danger)" }}>
            {perRound(finance.expensePerRound)}
          </div>
        </div>
        <div>
          <div className="label-muted">{t("financas.resultadoPorRodada")}</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
            {balance === null ? (
              <span className="muted">{t("comum.vazio")}</span>
            ) : (
              <Money value={balance} strong />
            )}
          </div>
        </div>
      </div>

      <table>
        <tbody>
          {lines.map(([chave, value, negative]) => (
            <tr key={chave}>
              <td className="muted">{t(chave)}</td>
              <td className="right">
                <span style={{ color: negative ? "var(--danger)" : "inherit" }}>
                  {dinheiro(value)}
                </span>
              </td>
            </tr>
          ))}
          {club.debt > 0 && (
            <tr>
              <td className="muted">{t("financas.dividaComBanco")}</td>
              <td className="right" style={{ color: "var(--danger)" }}>
                {dinheiro(-club.debt)}
              </td>
            </tr>
          )}
          <tr>
            <td className="muted">{t("financas.ingresso")}</td>
            <td className="right num">{dinheiro(club.ticketPrice)}</td>
          </tr>
          <tr>
            <td className="muted">{t("financas.capacidade")}</td>
            <td className="right num">{numero(club.stadiumCapacity)}</td>
          </tr>
        </tbody>
      </table>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Coluna da direita
// ---------------------------------------------------------------------------



/**
 * As rivalidades do clube.
 *
 * Saiu do cabeçalho para a seção do clube. Ela é referência e não leitura de
 * rodada: quem abre o jogo para jogar não precisa da barra de intensidade do
 * clássico na cara, e quem quer saber vai onde mora o resto do clube.
 */
export function RivalsCard({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  return (
    <Panel title={t("hub.rivalidades")}>
      <div className="grid" style={{ gap: 12 }}>
        {view.rivals.map((rival) => (
          <div key={rival.clubId} className="row" style={{ gap: 10 }}>
            <Crest colors={rival.colors} short={rival.short} size={30} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>{rival.name}</div>
              <div className="label-muted">{rotuloDoMotor(rival.labelKey)}</div>
              <div
                style={{
                  height: 4,
                  borderRadius: 999,
                  background: "var(--surface-3)",
                  marginTop: 5,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${rival.intensity}%`,
                    height: "100%",
                    background:
                      rival.intensity >= 85
                        ? "var(--danger)"
                        : rival.intensity >= 70
                          ? "var(--warning)"
                          : "var(--text-dim)",
                  }}
                />
              </div>
            </div>
            <span className="num" style={{ fontWeight: 700, color: "var(--text-muted)" }}>
              {rival.intensity}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export { Stars };
